/**
 * AdminBreakdown — dedicated detail page behind each AdminDashboard quick
 * stat tile. One URL per section: /admin/breakdown/:section where section
 * is one of: users, content, writers, pending.
 *
 * Each section renders its own list with the columns admins actually need
 * (vs the AdminDashboard tabs which mix moderation + analytics + roster).
 * Built as one file with four sub-views so the breadcrumbs, search bar,
 * sort selector and back-nav are shared.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Search, Users, BookOpen, PenTool, AlertTriangle,
  Crown, Flag, Coins, Star, ExternalLink, ShieldCheck, CheckCircle2, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch, checkAuth } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = {
  users: {
    label: 'All users',
    icon: Users,
    accent: 'from-blue-600/20 to-blue-700/10 border-blue-400/40',
    pillText: 'text-blue-200 bg-blue-500/10 border-blue-400/30',
  },
  content: {
    label: 'All content',
    icon: BookOpen,
    accent: 'from-purple-600/20 to-purple-700/10 border-purple-400/40',
    pillText: 'text-purple-200 bg-purple-500/10 border-purple-400/30',
  },
  writers: {
    label: 'Active writers',
    icon: PenTool,
    accent: 'from-emerald-600/20 to-emerald-700/10 border-emerald-400/40',
    pillText: 'text-emerald-200 bg-emerald-500/10 border-emerald-400/30',
  },
  pending: {
    label: 'Retain Pending',
    icon: AlertTriangle,
    accent: 'from-amber-600/20 to-amber-700/10 border-amber-400/40',
    pillText: 'text-amber-200 bg-amber-500/10 border-amber-400/30',
  },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

const fmtRel = (iso) => {
  if (!iso) return '—';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return fmtDate(iso);
};

const fmtDuration = (sec) => {
  if (!sec) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
};

// ─── Section components ────────────────────────────────────────────────

const RolePill = ({ role }) => {
  const map = {
    admin: 'text-red-300 bg-red-500/10 border-red-400/30',
    coadmin: 'text-orange-300 bg-orange-500/10 border-orange-400/30',
    writer: 'text-purple-300 bg-purple-500/10 border-purple-400/30',
    user: 'text-slate-300 bg-slate-500/10 border-slate-400/30',
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold rounded-full border px-2 py-0.5 ${map[role] || map.user}`}>
      {role}
    </span>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    published: 'text-emerald-300 bg-emerald-500/10 border-emerald-400/30',
    approved: 'text-emerald-300 bg-emerald-500/10 border-emerald-400/30',
    pending: 'text-amber-300 bg-amber-500/10 border-amber-400/30',
    hidden: 'text-rose-300 bg-rose-500/10 border-rose-400/30',
  };
  // Pocket-FM-style renaming: writer uploads aren't "approved", they're
  // "retained". So the visible label maps `pending → Retain Pending`,
  // `approved/published → Retained`, etc.
  const label = (
    status === 'pending' ? 'Retain Pending'
    : status === 'approved' ? 'Retained'
    : status === 'published' ? 'Retained · Published'
    : status === 'hidden' ? 'Hidden'
    : (status || 'unknown')
  );
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold rounded-full border px-2 py-0.5 ${map[status] || 'text-slate-300 bg-slate-500/10 border-slate-400/30'}`}>
      {label}
    </span>
  );
};

const UsersList = ({ rows, navigate }) => (
  <div className="space-y-2" data-testid="admin-breakdown-users-list">
    {rows.map((u) => (
      <article
        key={u.user_id}
        className="rounded-xl bg-slate-900/60 border border-slate-800 p-3 sm:p-4 hover:border-blue-500/40 transition flex flex-wrap items-center gap-3"
        data-testid={`breakdown-user-${u.user_id}`}
      >
        {u.picture ? (
          <img src={u.picture} alt="" className="w-10 h-10 rounded-full object-cover border border-slate-700" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-white truncate">{u.name}</h3>
            <RolePill role={u.role} />
            {u.is_top_tier && (
              <span className="text-[10px] uppercase font-bold text-amber-300 inline-flex items-center gap-0.5">
                <Crown className="w-3 h-3" /> Top Tier
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 truncate">{u.email} {u.country ? `· ${u.country}` : ''}</p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-x-4 gap-y-1 text-[11px] text-slate-300 ml-auto">
          <div><span className="text-slate-500 block">Joined</span>{fmtDate(u.created_at)}</div>
          <div><span className="text-slate-500 block">Last seen</span>{fmtRel(u.last_login_at)}</div>
          <div><span className="text-slate-500 block">Level</span><span className="font-bold tabular-nums">{u.level}</span></div>
          <div><span className="text-slate-500 block">XP</span><span className="font-bold tabular-nums">{u.xp.toLocaleString()}</span></div>
          <div><span className="text-slate-500 block">Coins</span><span className="font-bold tabular-nums text-amber-300 inline-flex items-center gap-0.5"><Coins className="w-3 h-3" />{u.coin_balance.toLocaleString()}</span></div>
        </div>
      </article>
    ))}
  </div>
);

const ContentList = ({ rows, navigate }) => (
  <div className="space-y-2" data-testid="admin-breakdown-content-list">
    {rows.map((c) => (
      <article
        key={c.ref_id}
        className="rounded-xl bg-slate-900/60 border border-slate-800 p-3 sm:p-4 hover:border-purple-500/40 transition flex flex-wrap items-center gap-3"
        data-testid={`breakdown-content-${c.ref_id}`}
      >
        {c.cover_image ? (
          <img src={c.cover_image} alt="" className="w-12 h-16 rounded object-cover border border-slate-700" />
        ) : (
          <div className="w-12 h-16 rounded bg-gradient-to-br from-purple-500 to-fuchsia-600" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => navigate(`/series/${c.ref_id}`)}
              className="text-sm font-bold text-white hover:text-indigo-200 truncate inline-flex items-center gap-1 text-left"
              data-testid={`breakdown-content-open-${c.ref_id}`}
            >
              {c.title} <ExternalLink className="w-3 h-3 opacity-60" />
            </button>
            <StatusPill status={c.status} />
            <span className="text-[10px] uppercase tracking-widest text-slate-400">{c.kind}</span>
            {c.genre && <span className="text-[10px] text-slate-500">· {c.genre}</span>}
            {c.has_original_ack && (
              <span title="Has originality acknowledgement" className="inline-flex items-center text-emerald-300">
                <ShieldCheck className="w-3 h-3" />
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 truncate">
            by <span className="text-slate-300">{c.writer_name}</span> · created {fmtRel(c.created_at)}
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-[11px] text-slate-300 ml-auto">
          {c.word_count > 0 && <div><span className="text-slate-500 block">Words</span>{c.word_count.toLocaleString()}</div>}
          {c.duration_seconds > 0 && <div><span className="text-slate-500 block">Length</span>{fmtDuration(c.duration_seconds)}</div>}
          <div><span className="text-slate-500 block">Comments</span><span className="font-bold tabular-nums">{c.comment_count}</span></div>
          <div>
            <span className="text-slate-500 block">Reports</span>
            <span className={`font-bold tabular-nums inline-flex items-center gap-0.5 ${c.report_count > 0 ? 'text-rose-300' : 'text-slate-300'}`}>
              {c.report_count > 0 && <Flag className="w-3 h-3" />}
              {c.report_count}
            </span>
          </div>
        </div>
      </article>
    ))}
  </div>
);

const WritersList = ({ rows, navigate }) => (
  <div className="space-y-2" data-testid="admin-breakdown-writers-list">
    {rows.map((w) => (
      <article
        key={w.user_id}
        className="rounded-xl bg-slate-900/60 border border-slate-800 p-3 sm:p-4 hover:border-emerald-500/40 transition flex flex-wrap items-center gap-3"
        data-testid={`breakdown-writer-${w.user_id}`}
      >
        {w.picture ? (
          <img src={w.picture} alt="" className="w-11 h-11 rounded-full object-cover border border-slate-700" />
        ) : (
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => navigate(`/writer/${w.user_id}`)}
              className="text-sm font-bold text-white hover:text-emerald-200 truncate"
              data-testid={`breakdown-writer-open-${w.user_id}`}
            >
              {w.pen_name || w.name}
            </button>
            <span className="text-[10px] uppercase tracking-widest text-emerald-300 inline-flex items-center gap-0.5">
              <Star className="w-3 h-3" /> Lv {w.writer_level}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 truncate">{w.email} {w.country ? `· ${w.country}` : ''}</p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-x-4 gap-y-1 text-[11px] text-slate-300 ml-auto">
          <div><span className="text-slate-500 block">Joined</span>{fmtDate(w.created_at)}</div>
          <div><span className="text-slate-500 block">Last upload</span>{fmtRel(w.last_upload_at)}</div>
          <div><span className="text-slate-500 block">Uploads</span><span className="font-bold tabular-nums">{w.uploads.total}</span></div>
          <div><span className="text-slate-500 block">Followers</span><span className="font-bold tabular-nums">{w.followers}</span></div>
          <div><span className="text-slate-500 block">XP</span><span className="font-bold tabular-nums">{w.writer_xp.toLocaleString()}</span></div>
          <div><span className="text-slate-500 block">Coins</span><span className="font-bold tabular-nums text-amber-300 inline-flex items-center gap-0.5"><Coins className="w-3 h-3" />{w.coin_balance.toLocaleString()}</span></div>
        </div>
      </article>
    ))}
  </div>
);

const PendingList = ({ rows, navigate, reload }) => {
  const [busy, setBusy] = useState({});

  const moderate = async (refId, action) => {
    setBusy((b) => ({ ...b, [refId]: action }));
    try {
      const endpoint = action === 'approve'
        ? `${BACKEND_URL}/api/admin/content/${refId}/approve`
        : `${BACKEND_URL}/api/admin/content/${refId}/reject`;
      const r = await authFetch(endpoint, { method: 'POST' });
      if (!r.ok) throw new Error('Moderate failed');
      toast.success(`Content ${action === 'approve' ? 'retained' : 'discarded'}.`);
      reload();
    } catch (e) {
      toast.error(e.message || 'Moderate failed');
    } finally {
      setBusy((b) => ({ ...b, [refId]: null }));
    }
  };

  return (
    <div className="space-y-2" data-testid="admin-breakdown-pending-list">
      {rows.map((c) => (
        <article
          key={c.ref_id}
          className="rounded-xl bg-slate-900/60 border border-slate-800 p-3 sm:p-4 hover:border-amber-500/40 transition flex flex-wrap items-center gap-3"
          data-testid={`breakdown-pending-${c.ref_id}`}
        >
          {c.cover_image ? (
            <img src={c.cover_image} alt="" className="w-12 h-16 rounded object-cover border border-slate-700" />
          ) : (
            <div className="w-12 h-16 rounded bg-gradient-to-br from-amber-500 to-orange-600" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(`/series/${c.ref_id}`)}
                className="text-sm font-bold text-white hover:text-amber-200 truncate inline-flex items-center gap-1 text-left"
                data-testid={`breakdown-pending-open-${c.ref_id}`}
              >
                {c.title} <ExternalLink className="w-3 h-3 opacity-60" />
              </button>
              <span className="text-[10px] uppercase tracking-widest text-slate-400">{c.kind}</span>
              {c.has_original_ack ? (
                <span title="Has originality acknowledgement" className="inline-flex items-center text-emerald-300">
                  <ShieldCheck className="w-3 h-3" />
                </span>
              ) : (
                <span title="No originality acknowledgement on file" className="inline-flex items-center text-rose-300">
                  <AlertTriangle className="w-3 h-3" />
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 truncate">
              by <span className="text-slate-300">{c.writer_name}</span> · submitted {fmtRel(c.submitted_at)}
              {c.word_count > 0 && <> · {c.word_count.toLocaleString()} words</>}
              {c.duration_seconds > 0 && <> · {fmtDuration(c.duration_seconds)}</>}
            </p>
          </div>
          <div className="flex gap-1.5 ml-auto">
            <button
              type="button"
              onClick={() => moderate(c.ref_id, 'approve')}
              disabled={!!busy[c.ref_id]}
              className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold inline-flex items-center gap-1 disabled:bg-slate-700"
              data-testid={`breakdown-pending-approve-${c.ref_id}`}
            >
              {busy[c.ref_id] === 'approve' ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Retain
            </button>
            <button
              type="button"
              onClick={() => moderate(c.ref_id, 'reject')}
              disabled={!!busy[c.ref_id]}
              className="px-3 py-1.5 rounded-md border border-rose-400/40 text-rose-200 hover:bg-rose-500/10 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50"
              data-testid={`breakdown-pending-reject-${c.ref_id}`}
            >
              {busy[c.ref_id] === 'reject' ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />} Discard
            </button>
          </div>
        </article>
      ))}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────
const AdminBreakdown = () => {
  const { section } = useParams();
  const navigate = useNavigate();
  const meta = SECTIONS[section] || SECTIONS.users;
  const [me, setMe] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('default');

  useEffect(() => {
    (async () => {
      const u = await checkAuth();
      if (!u) { navigate('/admin-login'); return; }
      if (!['admin', 'coadmin'].includes(u.role)) {
        toast.error('Admin only');
        navigate(-1);
        return;
      }
      setMe(u);
    })();
  }, [navigate]);

  const load = useCallback(async () => {
    if (!me || !SECTIONS[section]) return;
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/breakdown/${section}`);
      if (r.ok) setData(await r.json());
      else setData({ items: [], total: 0 });
    } finally { setLoading(false); }
  }, [me, section]);

  useEffect(() => { load(); }, [load]);

  // Searchable + sortable view.
  const view = useMemo(() => {
    let items = data.items || [];
    const q = query.trim().toLowerCase();
    if (q) {
      items = items.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
    }
    if (sort === 'newest') items = [...items].sort((a, b) => String(b.created_at || b.submitted_at || '').localeCompare(String(a.created_at || a.submitted_at || '')));
    if (sort === 'oldest') items = [...items].sort((a, b) => String(a.created_at || a.submitted_at || '').localeCompare(String(b.created_at || b.submitted_at || '')));
    if (section === 'users' && sort === 'level') items = [...items].sort((a, b) => (b.level || 0) - (a.level || 0));
    if (section === 'writers' && sort === 'uploads') items = [...items].sort((a, b) => (b.uploads?.total || 0) - (a.uploads?.total || 0));
    if (section === 'writers' && sort === 'followers') items = [...items].sort((a, b) => (b.followers || 0) - (a.followers || 0));
    if (section === 'content' && sort === 'reports') items = [...items].sort((a, b) => (b.report_count || 0) - (a.report_count || 0));
    return items;
  }, [data, query, sort, section]);

  if (!SECTIONS[section]) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10 text-center">
        <p className="text-slate-300 text-sm">Unknown section.</p>
        <button onClick={() => navigate('/admin')} className="mt-4 text-indigo-300 hover:text-white text-sm">← Back to dashboard</button>
      </main>
    );
  }

  const SectionIcon = meta.icon;

  return (
    <main className="max-w-5xl mx-auto px-4 py-6 pb-32" data-testid={`admin-breakdown-page-${section}`}>
      {/* Breadcrumb / back */}
      <button
        type="button"
        onClick={() => navigate('/admin')}
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white mb-4"
        data-testid="admin-breakdown-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Admin Dashboard
      </button>

      {/* Header banner */}
      <header className={`rounded-2xl bg-gradient-to-br ${meta.accent} border p-5 sm:p-6 mb-5`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <SectionIcon className="w-7 h-7 text-white" />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">{meta.label}</h1>
              <p className="text-[12px] text-white/70">
                {loading ? 'Loading…' : `${data.total.toLocaleString()} total · showing ${view.length.toLocaleString()}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {section === 'users' && (
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-slate-900/80 border border-slate-700 text-slate-200 text-xs rounded-full px-3 py-1.5 focus:outline-none"
                data-testid="admin-breakdown-sort"
              >
                <option value="default">Newest sign-ups first</option>
                <option value="oldest">Oldest first</option>
                <option value="level">Highest level</option>
              </select>
            )}
            {section === 'content' && (
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-slate-900/80 border border-slate-700 text-slate-200 text-xs rounded-full px-3 py-1.5 focus:outline-none"
                data-testid="admin-breakdown-sort"
              >
                <option value="default">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="reports">Most reported</option>
              </select>
            )}
            {section === 'writers' && (
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-slate-900/80 border border-slate-700 text-slate-200 text-xs rounded-full px-3 py-1.5 focus:outline-none"
                data-testid="admin-breakdown-sort"
              >
                <option value="default">Newest joined</option>
                <option value="uploads">Most uploads</option>
                <option value="followers">Most followers</option>
              </select>
            )}
          </div>
        </div>
      </header>

      {/* Search bar */}
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${meta.label.toLowerCase()}…`}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-900/70 border border-slate-700 text-white text-sm focus:outline-none focus:border-indigo-500"
          data-testid="admin-breakdown-search"
        />
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading the list…
        </p>
      ) : view.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-10 text-center" data-testid="admin-breakdown-empty">
          Nothing here{query ? ' matches your search' : ' yet'}.
        </p>
      ) : (
        <>
          {section === 'users' && <UsersList rows={view} navigate={navigate} />}
          {section === 'content' && <ContentList rows={view} navigate={navigate} />}
          {section === 'writers' && <WritersList rows={view} navigate={navigate} />}
          {section === 'pending' && <PendingList rows={view} navigate={navigate} reload={load} />}
        </>
      )}
    </main>
  );
};

export default AdminBreakdown;
